<template>
  <div class="w-100">
    <template v-if="label">
      <FieldLabel>{{ label }}</FieldLabel>
    </template>
    <div
      :ref="`dropdown-input-${fieldName}`"
      class="dropdown-wrapper d-flex position-relative"
      :class="[disabled ? 'dropdown-disabled' : 'dropdown-enabled', { focus: focus }]"
      :style="`width: ${width}`"
    >
      <input
        :ref="`dropdown-input-${fieldName}`"
        class="input w-100 px-2 position-relative"
        type="text"
        readonly="true"
        :value="fieldValueDisplay"
        :disabled="disabled"
        @click="toggleDropdown"
        @keydown="keyHandler"
        @focus="focus = true"
        @blur="focus = false"
      >
      <button
        class="dropdown-icon position-absolute"
        :class="{open: dropdownIsOpen}"
        tabindex="-1"
        :disabled="disabled"
        @click="toggleDropdown"
      >
        <i 
          class="fa" 
          :class="[dropdownIsOpen ? 'fa-caret-up' : 'fa-caret-down']"
        />
      </button>
    </div>
    <SelectionList
      :field-name="fieldName"
      :width="selectionListWidth()"
      :open="dropdownIsOpen"
      :item-list="itemList"
      :select-item="selectItem"
      :highlighted-index-changed="highlightedIndexChanged"
      :class="{'selection-list-open': dropdownIsOpen}"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { KEY_CODES } from '../../general/helper'
import { IItemList } from '../interfaces'
import FieldLabel from './FieldLabel.vue'
import SelectionList from './SelectionList.vue'

export default defineComponent({
  components: { FieldLabel, SelectionList },

  props: {
    disabled: { type: Boolean, default: false },
    width: { type: String, default: '100%' },
    label: { type: String, default: null },
    placeholder: { type: String, default: '' },
    fieldName: { type: String, default: '' },
    fieldValue: { type: String, default: '' },
    valueChanged: { type: Function, default: () => console.log('valueChanged not implemented') }, // eslint-disable-line no-console
    itemList: {
      type: Array<IItemList>,
      default: []
    },
  },

  data: () => {
    return {
      focus: false,
      dropdownIsOpen: false,
      highlightedIndex: -1,
    }
  },

  computed: {
    fieldValueDisplay() {
      for (const entry of this.itemList) {
        if (entry.key === this.fieldValue) {
          return entry.inputDisplay || entry.listDisplay
        }
      }
      return null
    },
  },

  methods: {
    showDropdown() {
      this.dropdownIsOpen = true
    },

    hideDropdown() {
      this.dropdownIsOpen = false
    },

    toggleDropdown() {
      const dropDownRef = this.$refs[`dropdown-input-${this.fieldName}`] as HTMLElement
      
      if (dropDownRef) {
        this.dropdownIsOpen = !this.dropdownIsOpen
        dropDownRef.focus()
      }
    },

    keyHandler(event: KeyboardEvent) {
      switch (event.code) {
        case KEY_CODES.ENTER:
        case KEY_CODES.ARROW_DOWN:
        case KEY_CODES.SPACE:
          this.showDropdown()
          break

        case KEY_CODES.ESCAPE:
        case KEY_CODES.TAB:
          this.hideDropdown()
          break

        default:
          for (const item of this.itemList) {
            if (item.key === event.key) {
              this.selectItem(item)
              break
            }
          }
          break
      }
    },

    selectItem(item: IItemList) {
      if (!item) {
        this.hideDropdown()
        return
      }
      if (!item.unavailable && !item.divider) {
        this.hideDropdown()
        this.valueChanged(this.fieldName, item.key, item)
        
      } else {
        const dropDownRef = this.$refs[`dropdown-input-${this.fieldName}`] as HTMLElement
        if (dropDownRef) {
          dropDownRef.focus()
        }
      }
    },

    selectionListWidth() {
      const input = this.$refs[`dropdown-input-${this.fieldName}`] as HTMLElement

      return !!input ? input.getClientRects()[0].width : 150
    },

    highlightedIndexChanged(index: number) {
      this.highlightedIndex = index
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.dropdown-wrapper {
  height: 32px;
}

.dropdown-enabled {
  border: 2px solid $color-expinet-dark-gray;

  &:hover {
    border: 2px solid $color-expinet-blue-h;

    .dropdown-icon {
      background-color: $color-expinet-background;
    }
  }

  &.focus {
    border: 2px solid $color-expinet-blue;
  }

  .input:focus {
    outline: 0;
  }

  button:focus {
    outline: none;
  }
}

.dropdown-disabled {
  border: 2px solid $color-aida-gray;

  .input {
    cursor: default;
    background-color: $color-aida-light-gray;
    color: $color-aida-darker-gray;
  }

  button {
    cursor: default;
    background-color: $color-aida-light-gray;
  }

  .dropdown-icon {
    color: $color-aida-gray;
  }
}

.input {
  color: $color-aida-black;
  box-shadow: none;
  border: none;
}

button {
  top: 0px;
  right: 0px;
  background: none;
  border: none;
  width: 23px;
  height: 100%;
  cursor: pointer;
  flex: 0;
  font-size: 16px;
}

.selection-list-open {
  border: 2px solid $color-expinet-blue;
  border-top: none !important;
}

.dropdown-icon {
  width: 30px;
  color: $color-expinet-blue;
  
  &.open {
    background-color: $color-expinet-background;
  }
}
</style>
